export interface DurationDaysValueLabelProps {
  value: string | number | null | undefined;
  fontSize?: 'xs' | 'table' | 'sm' | 'base' | 'lg';
  className?: string;
  /** Enable CSS truncation with ellipsis and title tooltip */
  truncate?: boolean;
}

const fontSizeClasses = {
  xs: 'text-xs',
  table: 'text-[13px]',
  sm: 'text-sm',
  base: 'text-base',
  lg: 'text-lg',
};

/**
 * Format a duration in days to a smart human-readable string.
 * Auto-picks the best unit: "18m", "2h 30m", "3d 5h", "14d"
 * Negative values are prefixed with "-" (e.g., "-2d 12h").
 */
function formatDuration(days: number): string {
  const sign = days < 0 ? '-' : '';
  const totalMinutes = Math.round(Math.abs(days) * 24 * 60);

  if (totalMinutes === 0) {
    // Actual zero input → "0m"; tiny non-zero input that rounds to 0 → "<1m"
    return days === 0 ? '0m' : `${sign}<1m`;
  }

  if (totalMinutes < 60) {
    return `${sign}${totalMinutes}m`;
  }

  const totalHours = Math.floor(totalMinutes / 60);
  const remainingMinutes = totalMinutes % 60;

  if (totalHours < 24) {
    if (remainingMinutes === 0) {
      return `${sign}${totalHours}h`;
    }
    return `${sign}${totalHours}h ${remainingMinutes}m`;
  }

  const totalDays = Math.floor(totalHours / 24);
  const remainingHours = totalHours % 24;

  if (remainingHours === 0) {
    return `${sign}${totalDays}d`;
  }
  return `${sign}${totalDays}d ${remainingHours}h`;
}

/**
 * Display component for duration_days values.
 * Formats fractional days into human-readable durations.
 *
 * @example
 * <DurationDaysValueLabel value="0.012269" />
 * // Renders: "18m"
 *
 * <DurationDaysValueLabel value="2.5" />
 * // Renders: "2d 12h"
 */
export const DurationDaysValueLabel: React.FC<DurationDaysValueLabelProps> = ({
  value,
  fontSize = 'sm',
  className = '',
  truncate,
}) => {
  const sizeClass = fontSizeClasses[fontSize];
  const truncateClasses = truncate
    ? 'block overflow-hidden text-ellipsis whitespace-nowrap'
    : '';

  if (value === null || value === undefined || value === '') {
    return <span className={`${sizeClass} ${className}`}>—</span>;
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return (
      <span
        className={`${sizeClass} ${truncateClasses} ${className}`}
        title={truncate ? String(value) : undefined}
      >
        {String(value)}
      </span>
    );
  }

  const formatted = formatDuration(numValue);
  const exactDays = numValue.toFixed(2);
  const tooltip = `${exactDays} days`;

  return (
    <span
      className={`${sizeClass} ${truncateClasses} ${className}`}
      title={tooltip}
    >
      {formatted}
    </span>
  );
};
